import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import { sortEnrollmentNo } from "../../utils/enrollmentSorter";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { FiAlertCircle } from "react-icons/fi";

const ViewAttendenceByDate = () => {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjectObjs, setFilteredSubjectObjs] = useState([]);
  const [noStudentsMessage, setNoStudentsMessage] = useState("");
  const [branches, setBranches] = useState([]);
  const [sections, setSections] = useState(["A", "B", "C", "D"]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedRegulation, setSelectedRegulation] = useState("");
  const [studentRegulations, setStudentRegulations] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSections = async () => {
      try {
        let params = {};
        if (selectedBranch) params.branch = selectedBranch;
        if (selectedSemester) params.semester = selectedSemester;
        const res = await axios.get(`${baseApiURL()}/section/getSectionsByBranchAndSemester`, { params });
        if (res.data.success && res.data.sections?.length > 0) {
          setSections(res.data.sections);
        }
      } catch (err) {
        console.error("Error fetching dynamic sections:", err);
      }
    };
    fetchSections();
  }, [selectedBranch, selectedSemester]);
  const [editModal, setEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    branch: "",
    semester: "",
    section: "",
    subject: "",
    date: null,
    period: "",
  });

  const [allSubjects, setAllSubjects] = useState([]);

  useEffect(() => {
    const fetchStudentRegulations = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/student/details/getRegulations`);
        if (response.data.success) {
          setStudentRegulations(response.data.regulations || []);
        }
      } catch (error) {
        console.error("Error fetching student regulations:", error);
      }
    };
    fetchStudentRegulations();
  }, []);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/subject/getSubject`);
        if (response.data.success) {
          setAllSubjects(response.data.subject);
        }
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
    };

    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/branch/getBranch`);
        if (response.data.success) {
          setBranches(response.data.branches.map(b => b.name));
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };

    fetchSubjects();
    fetchBranches();
  }, []);

  useEffect(() => {
    const filterSubjectsByStudentRegulation = async () => {
      if (selectedBranch && selectedSemester) {
        let studentReg = null;
        let hasStudents = false;
        try {
          const studentRes = await axios.post(`${baseApiURL()}/student/details/getDetails`, {
            branch: selectedBranch,
            semester: Number(selectedSemester)
          });
          if (studentRes.data.success && studentRes.data.user && studentRes.data.user.length > 0) {
            hasStudents = true;
            studentReg = studentRes.data.user[0].regulation;
          }
        } catch (err) {
          console.warn("Could not fetch students for regulation check:", err);
        }

        if (!hasStudents) {
          setSubjects([]);
          setFilteredSubjectObjs([]);
          setNoStudentsMessage("no students are there for that semester");
          setSelectedSubject("");
          return;
        }

        setNoStudentsMessage("");

        const filtered = allSubjects.filter(
          (subject) =>
            (subject.branch?.name === selectedBranch || subject.branch === selectedBranch) &&
            String(subject.semester) === String(selectedSemester) &&
            (!studentReg || subject.regulation?.toUpperCase() === studentReg.toUpperCase())
        );
        setFilteredSubjectObjs(filtered);
        setSubjects(filtered.map(sub => sub.name));
      } else {
        let filtered = allSubjects;
        if (selectedBranch) {
          filtered = filtered.filter(s => (s.branch?.name || s.branch) === selectedBranch);
        }
        if (selectedSemester) {
          filtered = filtered.filter(s => String(s.semester) === String(selectedSemester));
        }
        if (selectedRegulation) {
          filtered = filtered.filter(s => s.regulation === selectedRegulation);
        }
        setFilteredSubjectObjs(filtered);
        setSubjects(filtered.map(sub => sub.name));
        setNoStudentsMessage("");
      }
    };
    filterSubjectsByStudentRegulation();
  }, [selectedBranch, selectedSemester, selectedRegulation, allSubjects]);

  const fetchAttendanceByDate = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${baseApiURL()}/attendence/getByDate`;

      const params = new URLSearchParams();
      if (selectedSubject) params.append("subject", selectedSubject);
      if (selectedBranch) params.append("branch", selectedBranch);
      if (selectedSemester) params.append("semester", selectedSemester);
      if (selectedSection) params.append("section", selectedSection);
      if (startDate) params.append("startDate", startDate.toISOString());
      if (endDate) params.append("endDate", endDate.toISOString());

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url);
      if (response.data.success) {
        setAttendanceRecords(response.data.attendance);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError("Failed to fetch attendance records. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedSubject, selectedBranch, selectedSemester, selectedSection, startDate, endDate]);

  useEffect(() => {
    fetchAttendanceByDate();
  }, [fetchAttendanceByDate]);

  const handleDeleteAttendance = async (id) => {
    if (window.confirm("Are you sure you want to delete this attendance record?")) {
      try {
        const response = await axios.delete(`${baseApiURL()}/attendence/delete/${id}`);
        if (response.data.success) {
          toast.success(response.data.message || "Attendance deleted successfully");
          setAttendanceRecords(prevRecords =>
            prevRecords.filter(record => record._id !== id)
          );
        } else {
          toast.error(response.data.message || "Failed to delete attendance");
        }
      } catch (error) {
        console.error('Delete error:', error);
        toast.error(
          error.response?.data?.message ||
          error.message ||
          "Failed to delete attendance"
        );
      }
    }
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    setEditForm({
      branch: record.branch || "",
      semester: record.semester || "",
      section: record.section || "",
      subject: record.subject || "",
      date: record.date ? new Date(record.date) : null,
      period: record.period || "",
    });
    setEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditDateChange = (date) => {
    setEditForm(prev => ({ ...prev, date }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        branch: editForm.branch,
        semester: editForm.semester,
        section: editForm.section,
        subject: editForm.subject,
        date: editForm.date ? editForm.date.toISOString() : null,
        period: editForm.period,
      };
      const response = await axios.put(
        `${baseApiURL()}/attendence/update/${editRecord._id}`,
        payload
      );
      if (response.data.success) {
        toast.success("Attendance updated successfully");
        setAttendanceRecords(prev =>
          prev.map(rec =>
            rec._id === editRecord._id
              ? { ...rec, ...payload, date: editForm.date }
              : rec
          )
        );
        setEditModal(false);
      } else {
        toast.error(response.data.message || "Failed to update attendance");
      }
    } catch (error) {
      toast.error("Failed to update attendance");
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
  };

  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
  };

  const handleSemesterChange = (e) => {
    setSelectedSemester(e.target.value);
  };

  const handleSectionChange = (e) => {
    setSelectedSection(e.target.value);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (endDate && date && endDate < date) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const clearFilters = () => {
    setSelectedSubject("");
    setSelectedBranch("");
    setSelectedSemester("");
    setSelectedSection("");
    setStartDate(null);
    setEndDate(null);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400 mb-3"></div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Loading attendance records...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-2xl text-xs">
        <strong className="font-bold">Error: </strong>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Filters Section */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Branch</label>
            <select
              value={selectedBranch}
              onChange={handleBranchChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option value="">All Branches</option>
              {branches.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Semester</label>
            <select
              value={selectedSemester}
              onChange={handleSemesterChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option value="">All Semesters</option>
              {[1,2,3,4,5,6,7,8].map((sem) => (
                <option key={sem} value={sem}>Semester {sem}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Regulation</label>
            <input
              type="text"
              value={selectedRegulation}
              readOnly
              placeholder="Auto-fetched..."
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 cursor-not-allowed font-bold text-indigo-600 dark:text-indigo-400"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Section</label>
            <select
              value={selectedSection}
              onChange={handleSectionChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option value="">All Sections</option>
              {sections.map((section) => (
                <option key={section} value={section}>Section {section}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Subject</label>
            <select
              value={selectedSubject}
              onChange={handleSubjectChange}
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            >
              <option value="">All Subjects</option>
              {filteredSubjectObjs.map((subjectObj) => (
                <option key={subjectObj._id} value={subjectObj.name}>
                  {subjectObj.name} {subjectObj.code ? `(${subjectObj.code})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {noStudentsMessage && (
          <div className="p-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-300 text-xs flex items-center space-x-1.5 w-full">
            <FiAlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="font-medium">{noStudentsMessage}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 dark:border-slate-800">
          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">From Date</label>
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText="Select start date"
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
              maxDate={new Date()}
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">To Date</label>
            <DatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
              placeholderText="Select end date"
              className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-bold text-xs py-2 px-4 rounded-xl transition cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      {attendanceRecords.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs p-8 text-center text-slate-400">
          <p className="text-xs font-medium">No attendance records found for the selected filters.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
              <thead className="bg-slate-50 dark:bg-slate-950">
                <tr>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Enrollment No</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Branch</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Semester</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Section</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Subject</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Period</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Date</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                {attendanceRecords
                  .filter(record =>
                    (!selectedBranch || record.branch === selectedBranch) &&
                    (!selectedSemester || String(record.semester) === String(selectedSemester)) &&
                    (!selectedSection || record.section === selectedSection) &&
                    (!selectedSubject || record.subject === selectedSubject)
                  )
                  .sort(sortEnrollmentNo)
                  .map((record) => (
                    <tr key={record._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-800 dark:text-slate-200">
                      <td className="px-5 py-3.5 whitespace-nowrap font-mono font-bold text-indigo-600 dark:text-indigo-400">{record.enrollmentNo}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap font-medium text-slate-900 dark:text-white">{record.name}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">{record.branch}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">{record.semester}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">{record.section}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">{record.subject}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">{record.period}</td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {new Date(record.date).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditModal(record)}
                            className="text-indigo-600 dark:text-indigo-400 hover:underline font-bold cursor-pointer"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteAttendance(record._id)}
                            className="text-rose-600 dark:text-rose-400 hover:underline font-bold cursor-pointer"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-950/75 backdrop-blur-xs z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="border-b border-slate-100 dark:border-slate-800 pb-3">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Edit Attendance Record</h3>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Branch</label>
                <select
                  name="branch"
                  value={editForm.branch}
                  onChange={handleEditChange}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  required
                >
                  <option value="">Select Branch</option>
                  {branches.map((branch) => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Semester</label>
                <select
                  name="semester"
                  value={editForm.semester}
                  onChange={handleEditChange}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  required
                >
                  <option value="">Select Semester</option>
                  {[1,2,3,4,5,6,7,8].map((sem) => (
                    <option key={sem} value={sem}>Semester {sem}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Section</label>
                <select
                  name="section"
                  value={editForm.section}
                  onChange={handleEditChange}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  required
                >
                  <option value="">Select Section</option>
                  {sections.map((section) => (
                    <option key={section} value={section}>Section {section}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Subject</label>
                <select
                  name="subject"
                  value={editForm.subject}
                  onChange={handleEditChange}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  required
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Period</label>
                <input
                  type="text"
                  name="period"
                  value={editForm.period}
                  onChange={handleEditChange}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Date</label>
                <DatePicker
                  selected={editForm.date}
                  onChange={handleEditDateChange}
                  className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white"
                  required
                  maxDate={new Date()}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditModal(false)}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAttendenceByDate;